import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, X, TrendingUp, Package, Store, FileText, ChevronRight } from 'lucide-react';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { useAuth } from '../context/AuthContext';

/**
 * GlobalSearchBar - Enhanced search with autocomplete and contextual scoping
 * 
 * Features:
 * - Instant autocomplete suggestions (products, categories, vendors)
 * - Contextual search scoping based on current page
 * - Keyboard navigation
 * - Trending searches
 */
const GlobalSearchBar = ({ className = '', placeholder = 'Search the marketplace...' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { apiCall } = useAuth();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [trending, setTrending] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [scope, setScope] = useState('global');
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // Determine contextual scope based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/help') || path.includes('/faq')) {
      setScope('help');
    } else if (path.includes('/vendors')) {
      setScope('vendors');
    } else if (path.includes('/customers')) {
      setScope('customers');
    } else if (path.includes('/orders')) {
      setScope('orders');
    } else {
      setScope('global');
    }
  }, [location.pathname]);

  // Fetch trending searches on mount
  useEffect(() => {
    const fetchTrending = async () => {
      try {
        const data = await apiCall('/api/search/trending?limit=5');
        setTrending(data || []);
      } catch (error) {
      }
    };
    fetchTrending();
  }, []);

  // Debounced search for autocomplete
  const fetchSuggestions = useCallback(async (searchQuery) => {
    if (searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const data = await apiCall(`/api/search/autocomplete?q=${encodeURIComponent(searchQuery)}&scope=${scope}&limit=10`);
      setSuggestions(data || []);
    } catch (error) {
      console.error('Search autocomplete error:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [apiCall, scope]);

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setSelectedIndex(-1);

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce search
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 150);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => 
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && suggestions[selectedIndex]) {
        handleSelectSuggestion(suggestions[selectedIndex]);
      } else if (query.trim()) {
        handleSearch();
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  // Handle search submission
  const handleSearch = () => {
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}&scope=${scope}`);
      setIsOpen(false);
    }
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion) => {
    if (suggestion.type === 'product') {
      navigate(`/listings/products/${suggestion.id}`);
    } else if (suggestion.type === 'category') {
      navigate(`/search?q=${encodeURIComponent(suggestion.text)}&category=${suggestion.id}`);
    } else if (suggestion.type === 'vendor') {
      navigate(`/vendors/${suggestion.id}`);
    } else if (suggestion.type === 'customer') {
      navigate(`/customers/${suggestion.id}`);
    } else if (suggestion.type === 'content') {
      navigate(`/help/${suggestion.id}`);
    } else {
      setQuery(suggestion.text);
      handleSearch();
    }
    setIsOpen(false);
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get icon for suggestion type
  const getSuggestionIcon = (type) => {
    switch (type) {
      case 'product': return <Package className="h-4 w-4 text-gray-400" />;
      case 'vendor': return <Store className="h-4 w-4 text-gray-400" />;
      case 'customer': return <Search className="h-4 w-4 text-emerald-500" />;
      case 'content': return <FileText className="h-4 w-4 text-gray-400" />;
      case 'category': return <ChevronRight className="h-4 w-4 text-gray-400" />;
      default: return <Search className="h-4 w-4 text-gray-400" />;
    }
  };

  // Get scope label
  const getScopeLabel = () => {
    switch (scope) {
      case 'help': return 'Help & Support';
      case 'vendors': return 'Vendors';
      case 'customers': return 'Customers';
      case 'orders': return 'My Orders';
      default: return 'Marketplace';
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="pl-10 pr-20 h-10 w-full"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setSuggestions([]); }}
            className="absolute right-16 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
          >
            <X className="h-4 w-4 text-gray-400" />
          </button>
        )}
        <Badge 
          variant="secondary" 
          className="absolute right-2 top-1/2 -translate-y-1/2 text-xs cursor-pointer"
          onClick={() => setIsOpen(true)}
        >
          {getScopeLabel()}
        </Badge>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
          {/* Loading indicator */}
          {loading && (
            <div className="px-4 py-2 text-sm text-gray-500">
              Searching...
            </div>
          )}

          {/* Suggestions */}
          {!loading && suggestions.length > 0 && (
            <div className="py-2">
              {suggestions.map((suggestion, index) => (
                <button
                  key={`${suggestion.type}-${suggestion.id || index}`}
                  onClick={() => handleSelectSuggestion(suggestion)}
                  className={`w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50 text-left ${
                    selectedIndex === index ? 'bg-blue-50' : ''
                  }`}
                >
                  {suggestion.image ? (
                    <img 
                      src={suggestion.image} 
                      alt="" 
                      className="w-10 h-10 rounded object-cover"
                    />
                  ) : (
                    getSuggestionIcon(suggestion.type)
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {suggestion.text}
                    </p>
                    {suggestion.category && (
                      <p className="text-xs text-gray-500">in {suggestion.category}</p>
                    )}
                  </div>
                  {suggestion.price && (
                    <span className="text-sm font-medium text-gray-900">
                      ₹{suggestion.price.toLocaleString()}
                    </span>
                  )}
                  <Badge variant="outline" className="text-xs capitalize">
                    {suggestion.type}
                  </Badge>
                </button>
              ))}
            </div>
          )}

          {/* No results */}
          {!loading && query.length >= 2 && suggestions.length === 0 && (
            <div className="px-4 py-6 text-center">
              <p className="text-sm text-gray-500">No results found for "{query}"</p>
              <button
                onClick={handleSearch}
                className="mt-2 text-sm text-blue-600 hover:underline"
              >
                Search all results
              </button>
            </div>
          )}

          {/* Trending searches (when no query) */}
          {!loading && !query && trending.length > 0 && (
            <div className="py-2">
              <div className="px-4 py-2 flex items-center gap-2 text-xs font-medium text-gray-500 uppercase">
                <TrendingUp className="h-3 w-3" />
                Trending Searches
              </div>
              {trending.map((term, index) => (
                <button
                  key={term.id || term.name || `${term.title || "term"}-${index}`}
                  onClick={() => { setQuery(term); fetchSuggestions(term); }}
                  className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50 text-left"
                >
                  <Search className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-700">{term}</span>
                </button>
              ))}
            </div>
          )}

          {/* Quick search button */}
          {query.trim() && (
            <div className="border-t px-4 py-2">
              <button
                onClick={handleSearch}
                className="w-full py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 flex items-center justify-center gap-2"
              >
                <Search className="h-4 w-4" />
                Search for "{query}"
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearchBar;
